import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  ToastProvider, ThemeProvider, useTheme, useResponsive, SessionCheck, SessionError,
} from '@parsina/framework';
import { KC_BASE_URL } from './src/lib/runtimeEnv.js';
import logo from 'url:./src/assets/logo.png';
import logoWhite from 'url:./src/assets/logo-white.png';
import { I18nProvider, useTranslation } from './src/i18n/I18nProvider.jsx';
import { narrow } from './src/i18n/config.js';
import {
  getToken, getRefresh, isTokenExpired,
  handleCallback, redirectToLogin, refreshAccessToken,
  clearTokens, clearRedirectGuard,
} from './src/lib/auth.js';
import { takeReturnPath } from './src/lib/session.js';
import { bffQuery, errorCode } from './src/lib/bffClient.js';
import { ClinicScopeProvider } from './src/hooks/useClinicScope.jsx';
import useSessionLifecycle from './src/hooks/useSessionLifecycle.js';
import { SessionExpiringDialog, SessionExpiredDialog } from './src/components/system/SessionDialog.jsx';
import DentistLayout from './src/components/common/DentistLayout.jsx';
import MobileShell from './src/components/mobile/MobileShell.jsx';
import TodayScreen from './src/screens/Today/index.jsx';
import PatientsScreen from './src/screens/Patients/index.jsx';
import PatientDetailScreen from './src/screens/PatientDetail/index.jsx';
import PrescriptionScreen from './src/screens/Prescription/index.jsx';
import VisitScreen from './src/screens/Visit/index.jsx';
import ProfileScreen from './src/screens/Profile/index.jsx';
import CreditsScreen from './src/screens/Credits/index.jsx';
import OnboardingScreen from './src/screens/Onboarding/index.jsx';
import { setupPWA } from './src/lib/registerSW.js';
import './styles.css';

// One boot query carrying the two facts the app cannot start without: whether
// this account has finished registering, and which accent/language to paint in.
//
// Both fields are UNGATED on the BFF — they have to be, because an account with
// no specialty yet is exactly the one that needs to reach onboarding.
const BOOT_QUERY = `
  query Boot {
    checkOnboardingStatus { isOnboarded isDentist }
    myAppearanceSettings { theme fontSize language }
  }
`;

const AppShell = () => {
  // ≤768px gets the mobile shell — bottom navigation and per-screen gradient
  // headers (DESIGN-CONCEPT-MOBILE §3) — and not the sidebar at a smaller
  // width. The ROUTES are identical: the same five, in the same order. Only the
  // chrome around them forks, which is the §1.1 rule ("layout system, page
  // anatomy, chrome: NOT shared") applied to the shell.
  const { isMobile } = useResponsive();
  const Shell = isMobile ? MobileShell : DentistLayout;

  // A session ending is app chrome, not a screen's concern — it can arrive
  // while any of the seven routes is open, and every one of them has unsaved
  // state worth not destroying. Mounted here, once, above the router's outlet.
  const session = useSessionLifecycle();

  return (
    <>
    <Shell>
      <Routes>
        {/* امروز is the landing route: the first thing a dentist sees is the
            clinic's own appointments, which is the whole point of phase 1. */}
        <Route path="/" element={<TodayScreen />} />
        <Route path="/patients" element={<PatientsScreen />} />
        <Route path="/patients/:id/:tab?" element={<PatientDetailScreen />} />
        <Route path="/prescription/:patientId" element={<PrescriptionScreen />} />
        {/* جلسهٔ درمان — the visit workspace (docs/dent-visit.md). Keyed on the
            appointment, because the appointment IS the visit. */}
        <Route path="/visit/:appointmentId" element={<VisitScreen />} />
        {/* اعتبار — where «اعتبار هوش مصنوعی کافی نیست» is answered. Every AI
            call in this app spends پارسه, and until this screen existed the
            copy sent the dentist to the doctor panel to top up. */}
        <Route path="/credits" element={<CreditsScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        {/* Seven routes, seven built screens. A route for لابراتوار or یادآوری
            would be a route to nothing — they are added with their screens,
            never before (CORE.md §8). */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
    {/* Fixed-position overlays, so they render as siblings of the shell on both
        surfaces rather than being threaded into either one. They deliberately
        sit OVER the app instead of replacing it — the screen underneath is the
        dentist's unsaved work, and it stays visible. */}
    {session.isExpiring && (
      <SessionExpiringDialog
        secondsLeft={session.secondsLeft}
        onExtend={session.extend}
        onDismiss={session.dismissWarning}
        busy={session.busy}
      />
    )}
    {session.isExpired && (
      <SessionExpiredDialog onSignIn={session.signInAgain} busy={session.busy} />
    )}
    </>
  );
};

const AuthGate = () => {
  const { t, setLocale } = useTranslation();
  // 'checking'  resolving tokens / the OIDC callback
  // 'booting'   authenticated, asking the BFF who this is
  // 'ready'     the app
  // 'onboarding' authenticated but no medical code yet
  // 'bootError' the boot query failed for a reason that is NOT auth
  // 'loop'      auth.js detected a redirect loop and bailed
  const [phase, setPhase] = React.useState('checking');
  const [attempt, setAttempt] = React.useState(0);
  const { setAppearance } = useTheme();

  // Held in a ref, NOT used as a dependency. @parsina/framework's ThemeProvider
  // memoises its context value on its own appearance state and rebuilds
  // `setAppearance` whenever that state changes — and its updater returns a new
  // object every call, even when the resolved values are identical. So naming
  // `setAppearance` in boot's dependency list is an infinite boot loop:
  //   boot() → applies the server's appearance → new local state →
  //   new setAppearance → new boot → the effect below (deps [boot]) refires →
  //   boot() → …
  // It fired ~141 GraphQL requests before the page painted once. `boot` needs to
  // CALL this, never to react to it, which is exactly what a ref expresses.
  const setAppearanceRef = React.useRef(setAppearance);
  setAppearanceRef.current = setAppearance;

  // Same ref discipline, same reason: I18nProvider rebuilds its context value
  // whenever the locale changes, so naming anything off it in boot's dependency
  // list re-arms the effect that calls boot.
  const setLocaleRef = React.useRef(setLocale);
  setLocaleRef.current = setLocale;

  const boot = React.useCallback(async () => {
    try {
      const data = await bffQuery(BOOT_QUERY);
      const saved = data?.myAppearanceSettings;
      // The server copy is the real record and wins over the local cache — it
      // is how the preference follows the dentist to another machine. Nulls
      // mean "not set", not "reset to default", so they are filtered out.
      if (saved?.theme || saved?.fontSize) {
        setAppearanceRef.current({
          ...(saved.theme ? { theme: saved.theme } : {}),
          ...(saved.fontSize ? { fontSize: saved.fontSize } : {}),
        });
      }
      // The server copy of the language preference wins over the device's, so
      // the choice follows the dentist to another machine.
      //
      // It has to be APPLIED, not merely written to storage. I18nProvider
      // resolves the locale ONCE, in a lazy state initialiser, and it mounts
      // ABOVE this component — so by the time boot() runs that resolution has
      // already happened, and a bare localStorage write changes nothing until
      // the next full page load. `setLocale` persists it too, so storage stays
      // in step with what is on screen.
      //
      // `narrow` first: the stored value is a free-text BCP-47-ish tag, so
      // 'fa-IR' has to become 'fa' or setLocale rejects it as unsupported and
      // the preference is silently dropped.
      const language = narrow(saved?.language);
      if (language) setLocaleRef.current(language);
      const status = data?.checkOnboardingStatus;
      if (!status?.isOnboarded) {
        setPhase('onboarding');
        return;
      }
      // 2026-08-08: a specialty check used to sit here and send a
      // general-medicine doctor to a dead-end screen. The app is open to every
      // onboarded doctor now, so being onboarded IS the whole test. The
      // specialty flag is still queried — the profile screen shows it — but it
      // no longer decides anything. See tests/openToAllDoctors.test.js.
      setPhase('ready');
    } catch (err) {
      const code = errorCode(err);
      if (code === 'UNAUTHENTICATED' || err?.status === 401) {
        redirectToLogin();
        return;
      }
      // Anything else is an OUTAGE, not a verdict about this account. It must
      // not be read as "this dentist has not onboarded" — showing the
      // registration wizard to someone already registered is how a stale token
      // turned into a second, duplicate account in the doctor app.
      console.error('[boot] failed:', code || err?.message);
      setPhase('bootError');
    }
    // Intentionally NO dependencies: everything boot touches is either a stable
    // setState, a module import, or the ref above. A dependency here re-arms the
    // effect that calls it.
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_error') === 'loop') {
      setPhase('loop');
      return;
    }
    (async () => {
      // Returning from Keycloak: exchange the code first.
      if (params.has('code') || params.has('state') || params.has('error')) {
        if (await handleCallback()) {
          clearRedirectGuard();
          // Put the dentist back where they were interrupted. Keycloak's
          // redirect_uri is the origin, so a re-login has always landed on
          // امروز — a shrug on a list screen and a real cost on
          // /visit/:appointmentId, where the dentist has to find the
          // appointment and re-open the session they were mid-way through.
          //
          // `replaceState`, not a navigation: BrowserRouter has not mounted
          // yet, so rewriting the address here is what its FIRST render reads.
          // Doing it afterwards would paint امروز and then jump, and would
          // leave the OIDC callback in the history stack for the back button.
          const returnTo = takeReturnPath();
          if (returnTo && returnTo !== '/') window.history.replaceState({}, '', returnTo);
          setPhase('booting');
          boot();
          return;
        }
      }
      const token = getToken();
      if (token && !isTokenExpired(token)) {
        clearRedirectGuard();
        setPhase('booting');
        boot();
        return;
      }
      if (getRefresh() && (await refreshAccessToken())) {
        clearRedirectGuard();
        setPhase('booting');
        boot();
        return;
      }
      redirectToLogin(); // navigates away; no state update needed
    })();
  }, [boot, attempt]);

  // The session screens are the kit's (packages/DESIGN.md §"Session check") —
  // the same card, motion and manual sign-in link as every other Parsina
  // panel. Only the words and the logo are this panel's.
  if (phase === 'loop') {
    return (
      <SessionError
        logo={logo}
        brandName={t('nav.brand.name')}
        title={t('auth.loopTitle')}
        body={t('auth.loopError')}
        actionLabel={t('auth.retry')}
        onAction={() => {
          clearTokens();
          clearRedirectGuard();
          window.location.href = '/';
        }}
        host={KC_BASE_URL.replace(/^https?:\/\//, '')}
      />
    );
  }

  if (phase === 'bootError') {
    return (
      <SessionError
        tone="warning"
        testId="boot-error"
        stateTestId="boot-error-state"
        logo={logo}
        brandName={t('nav.brand.name')}
        title={t('auth.bootFailed.title')}
        body={t('auth.bootFailed.body')}
        actionLabel={t('common.actions.retry')}
        onAction={() => { setPhase('checking'); setAttempt((n) => n + 1); }}
        host={KC_BASE_URL.replace(/^https?:\/\//, '')}
      />
    );
  }

  if (phase === 'checking' || phase === 'booting') {
    return (
      <SessionCheck
        logo={logoWhite}
        brandName={t('nav.brand.name')}
        panelName={t('nav.brand.panel')}
        title={phase === 'checking' ? t('auth.checking') : t('auth.loading')}
        subtitle={t('auth.checkingHint')}
        stuckLabel={t('auth.stuck')}
        manualLabel={t('auth.manualSignIn')}
        onManualSignIn={() => { redirectToLogin(); }}
      />
    );
  }

  if (phase === 'onboarding') {
    // Outside the router on purpose: there is no app to navigate around yet.
    return (
      <OnboardingScreen
        onComplete={() => {
          setPhase('booting');
          boot();
        }}
      />
    );
  }

  return (
    <ClinicScopeProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ClinicScopeProvider>
  );
};

const DocumentTitle = () => {
  const { t, locale } = useTranslation();
  React.useEffect(() => {
    document.title = t('app.title');
  }, [t, locale]);
  return null;
};

// Provider order is load-bearing:
//   ThemeProvider outermost — it applies the cached accent in a lazy
//     initialiser, i.e. BEFORE first paint, so a reload does not flash the
//     default accent while the boot query is still in flight.
//   I18nProvider above ToastProvider — the auth-error and onboarding screens
//     read t(), and so do the toasts.
const App = () => (
  <ThemeProvider>
    <I18nProvider>
      <ToastProvider>
        <DocumentTitle />
        <AuthGate />
      </ToastProvider>
    </I18nProvider>
  </ThemeProvider>
);

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

// After render, never awaited: an app that cannot install must still run.
setupPWA();
